import type { ProviderRegistry } from "@loumy/contracts";

// §3.3 ProviderRegistry — In-Memory, server-seitig. Auflösung NACH Slot (+ optional
// nach Provider-`id`). Pinning (D30/D34) geschieht im buildPipeline: der aufgelöste
// Adapter (id+version) + seine Auflösung werden als Literale in `params` eingefroren;
// die Registry liefert nur den Adapter. Erster registrierter Provider je Slot = Default.

export class ProviderResolutionError extends Error {
  constructor(
    readonly slot: string,
    readonly requestedId?: string,
  ) {
    super(`Kein Provider für Slot '${slot}'${requestedId ? ` mit id '${requestedId}'` : ""} registriert.`);
    this.name = "ProviderResolutionError";
  }
}

export class InMemoryProviderRegistry implements ProviderRegistry {
  /** slot → providerId → provider */
  private readonly slots = new Map<string, Map<string, object>>();
  private readonly defaults = new Map<string, string>();

  register(slot: string, p: object): void {
    const id = (p as { id?: unknown }).id;
    if (typeof id !== "string" || id.length === 0)
      throw new Error(`Provider für Slot '${slot}' braucht eine nicht-leere string-id (§3.3).`);
    let bySlot = this.slots.get(slot);
    if (!bySlot) {
      bySlot = new Map();
      this.slots.set(slot, bySlot);
    }
    bySlot.set(id, p);
    if (!this.defaults.has(slot)) this.defaults.set(slot, id); // erster = Default
  }

  resolve<T>(slot: string, name?: string): T {
    const bySlot = this.slots.get(slot);
    if (!bySlot || bySlot.size === 0) throw new ProviderResolutionError(slot, name);
    const id = name ?? this.defaults.get(slot);
    const p = id ? bySlot.get(id) : undefined;
    if (!p) throw new ProviderResolutionError(slot, name);
    return p as T;
  }

  /** Ist ein Provider im Slot (optional mit id) verfügbar? */
  has(slot: string, name?: string): boolean {
    const bySlot = this.slots.get(slot);
    if (!bySlot) return false;
    return name ? bySlot.has(name) : bySlot.size > 0;
  }
}
